import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import UserManagementData from '@/ts/UserManagementData';
import ErrorResponse from '@/ts/ErrorResponse';
import router from '@/router';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';
import { Vue } from 'vue-property-decorator';

@Module({name: 'UserManagement', namespaced: true})
export default class UserManagement extends VuexModule {
    public users: UserManagementData[] = [];
    public cachedUser: UserManagementData = null;
    public contactUsVisible = false;
    public allStaffUsers: UserManagementData[] = [];
    public allTenantUsers: UserManagementData[] = [];
    public allStaffRoles: any[] = [];
    public carrentTenantUser: any = null;
    public carrentStaffUser: any = null;
    public carrentDeleteUser: any = [];
    public addTenantFormVisible: boolean = false;
    public addTenantFormMode: string = '';
    public addUpcomingTenantFormVisible: boolean = false;
    public addUpcomingGuestFormVisible: boolean = false;
    public addStaffFormVisible: boolean = false;
    public editTenantFormVisible: boolean = false;
    public editStaffFormVisible: boolean = false;
    public ownershipFormVisible: boolean = false;
    public managementStatusOption: string = 'active';
    public activeUpcomingTenant: any = null;
    public activeUpcomingGuest: any = null;
    public assignedProjectWithUnit: any = null;
    public reassignTenantUserVisible: boolean = false;
    public inactiveUserId: any = null;
    public revertUpcomingTenantApplicationVisible: boolean = false;
    public cancelUpcomingTenantMoveInVisible: boolean = false;
    public cancelUpcomingGuestMoveInVisible: boolean = false;

    @Mutation
    public RESET_USER_MANAGEMENT_STORE_STATE() {
        this.users                        = [];
        this.cachedUser                   = null;
        this.contactUsVisible             = false;
        this.allStaffUsers                = [];
        this.allTenantUsers               = [];
        this.allStaffRoles                = [];
        this.carrentTenantUser            = null;
        this.carrentStaffUser             = null;
        this.carrentDeleteUser            = [];
        this.addTenantFormVisible         = false;
        this.addTenantFormMode            = '';
        this.addUpcomingTenantFormVisible = false;
        this.addUpcomingGuestFormVisible  = false;
        this.addStaffFormVisible          = false;
        this.editTenantFormVisible        = false;
        this.editStaffFormVisible         = false;
        this.ownershipFormVisible         = false;
        this.managementStatusOption       = 'active';
        this.activeUpcomingTenant         = null;
        this.activeUpcomingGuest         = null;
        this.assignedProjectWithUnit      = null;
        this.reassignTenantUserVisible    = false;
        this.inactiveUserId               = null;

        this.revertUpcomingTenantApplicationVisible = false;
        this.cancelUpcomingTenantMoveInVisible = false;
        this.cancelUpcomingGuestMoveInVisible = false;
    }

    get getActiveUpcomingTenant() {
        return this.activeUpcomingTenant;
    }

    get getActiveUpcomingGuest() {
        return this.activeUpcomingGuest;
    }

    get getAssignedProjectWithUnit() {
        return this.assignedProjectWithUnit;
    }

    get getManagementStatusOption() {
        return this.managementStatusOption;
    }

    get getContactUsVisible(): boolean {
        return this.contactUsVisible;
    }

    get getAllStaffRoles() {
        return this.allStaffRoles;
    }

    get getCarrentTenantUser() {
        return this.carrentTenantUser;
    }

    get getCurrentStaffUser() {
        return this.carrentStaffUser;
    }

    get getCarrentDeleteUser() {
        return this.carrentDeleteUser;
    }

    get getAddStaffFormVisible() {
        return this.addStaffFormVisible;
    }

    get getAddTenantFormVisible() {
        return this.addTenantFormVisible;
    }

    get getAddTenantFormMode() {
        return this.addTenantFormMode;
    }

    get getAddUpcomingTenantFormVisible() {
        return this.addUpcomingTenantFormVisible;
    }

    get getAddUpcomingGuestFormVisible() {
        return this.addUpcomingGuestFormVisible;
    }

    get getEditStaffFormVisible() {
        return this.editStaffFormVisible;
    }

    get getEditTenantFormVisible() {
        return this.editTenantFormVisible;
    }

    get getOwnershipFormVisible() {
        return this.ownershipFormVisible;
    }

    get getAllStaffUsers() {
        return this.allStaffUsers;
    }

    get getAllTenantUsers() {
        return this.allTenantUsers;
    }

    get getInactiveUserId() {
        return this.inactiveUserId;
    }

    get getReassignTenantUserVisible() {
        return this.reassignTenantUserVisible;
    }

    get getRevertUpcomingTenantApplicationVisible() {
        return this.revertUpcomingTenantApplicationVisible;
    }

    get getCancelUpcomingTenantMoveInVisible() {
        return this.cancelUpcomingTenantMoveInVisible;
    }

    get getCancelUpcomingGuestMoveInVisible() {
        return this.cancelUpcomingGuestMoveInVisible;
    }

    @Action
    public FETCH_MANAGEMENT_USERS() {
        return axios.get(Endpoints.Admin.Staff.STAFF)
            .then((response) => {
                store.commit(Mutations.SET_USER_MANAGEMENT_ALL_STAFF_USERS, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_TENANT_USERS(type: string) {
        return axios.get(Endpoints.Admin.Tenants.TENANT_USERS, {
            params: { type },
        })
            .then((response) => {
                store.commit(Mutations.SET_USER_MANAGEMENT_ALL_TENANT_USERS, response.data.data);
            }).catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_CONTACT_US(text: string) {
        axios.post(Endpoints.Admin.Support.SEND_CONTACT_US, { text })
            .then(() => {
                store.commit(Mutations.SET_CONTACT_US_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_TENANT(data: any) {
        axios.post(Endpoints.Admin.Tenants.TENANT_USERS, data)
            .then((response) => {
                store.dispatch(Actions.FETCH_CURRENT_PROPERTY, router.currentRoute.params.unitId);
                store.commit(Mutations.ADD_TENANT_TO_PROPERTY, response.data);

                if (response.data.tenantStatus === 'primary') {
                    store.commit(Mutations.UPDATE_ACTIVE_PROPERTY_DATES, {
                        propertyId: response.data.id,
                        vacantFrom: null,
                        moveInDate: response.data.property_roles[0].created_at,
                        releaseDate: null,
                    });
                }

                store.commit(Mutations.UPDATE_PROPERTY_OWNER, {
                    isNewOwner: response.data.tenantStatus === 'primary',
                    oldOwnedProperty : '',
                    newProperty: data.unit,
                    tenantId: response.data.id,
                });

                const userObject = {
                    userId: response.data.id,
                    roleId: response.data.property_roles[0].role_id,
                    firstName: response.data.person.first_name,
                    familyName: response.data.person.family_name,
                    phoneNumber: response.data.person.phone_number,
                };

                store.commit(Mutations.ADD_USER_TO_CACHE, userObject);

                store.commit(Mutations.ADD_PROPERTY_TENANT_TO_CACHE, {
                    userId: userObject.userId,
                    roleId: userObject.roleId,
                    propertyId: data.unit,
                });

                store.commit(Mutations.SET_ADD_TENANT_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_UPCOMING_TENANT(data: any) {
        axios.get(Endpoints.Admin.Tenants.UPCOMING_TENANT(data.propertyId, data.tenantId))
            .then((response) => {
                store.commit(Mutations.SET_ACTIVE_UPCOMING_TENANT, response.data.data);
                store.commit(Mutations.SET_ADD_UPCOMING_TENANT_VISIBILITY, true);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_UPCOMING_TENANT(data: any) {
        return axios.post(Endpoints.Admin.Tenants.ADD_UPCOMING_TENANT, data)
            .then((response) => {
                store.commit(Mutations.ADD_UPCOMING_TENANT, response.data);
                store.commit(Mutations.RESET_NEW_TENANT_PROJECT_WITH_UNIT);
                store.commit(Mutations.SET_ADD_UPCOMING_TENANT_VISIBILITY, false);

                const userObjects = {
                    userId: response.data.id,
                    roleId: store.getters['User/getCachedRoleId']('property-owner'),
                    firstName: response.data.person.first_name,
                    familyName: response.data.person.family_name,
                    phoneNumber: response.data.person.phone_number,
                };

                store.commit(Mutations.ADD_USER_TO_CACHE, userObjects);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_UPCOMING_TENANT(data: any) {
        return axios.patch(Endpoints.Admin.Tenants.UPCOMING_TENANT(data.unit, data.id), data)
            .then((response) => {
                store.commit(Mutations.SET_ADD_UPCOMING_TENANT_VISIBILITY, false);
                store.commit(Mutations.RESET_NEW_TENANT_PROJECT_WITH_UNIT);
                store.commit(Mutations.SET_ACTIVE_UPCOMING_TENANT, null);
                store.commit(Mutations.EDIT_UPCOMING_TENANT, response.data);

                store.commit(Mutations.UPDATE_CACHED_PERSON_DATA, {
                    id: response.data.id,
                    firstName: typeof response.data.person !== 'undefined' ? response.data.person.first_name : response.data.name,
                    familyName: typeof response.data.person !== 'undefined' ? response.data.person.family_name : '',
                    phoneNumber: typeof response.data.person !== 'undefined' ? response.data.person.phone_number : '',
                });
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_UPCOMING_GUEST(data: any) {
        return axios.post(Endpoints.Admin.Tenants.ADD_UPCOMING_GUEST(data.unit), data)
            .then((response) => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_PERSONAL_CHANGES(data: any) {
        axios.patch(Endpoints.Admin.Tenants.PROFILE_PERSONAL_DETAILS(data.id), data)
            .then(() => {
                NotifyHelpers.notify('personal_data_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_PROFILING_CHANGES(data: any) {
        axios.patch(Endpoints.Admin.Tenants.PROFILE_DETAILS(data.id), data)
            .then(() => {
                NotifyHelpers.notify('profiling_data_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_APPLICATION_CHANGES(data: any) {
        axios.patch(Endpoints.Admin.Tenants.PROFILE_APPLICATION_DETAILS(data.id), data)
            .then(() => {
                NotifyHelpers.notify('application_data_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_STAFF(data: any) {
        axios.post(Endpoints.Admin.Staff.STAFF, data)
            .then(() => {
                store.dispatch(Actions.FETCH_MANAGEMENT_USERS);
                store.commit(Mutations.SET_ADD_STAFF_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_MANAGEMENT_ALL_STAFF_ROLES() {
        axios.get(Endpoints.Admin.Roles.STAFF_ROLES)
            .then((response: any) => {
                store.commit(Mutations.SET_USER_MANAGEMENT_ALL_STAFF_ROLES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_STAFF_USER(data: any) {
        axios.get(Endpoints.Admin.Staff.SINGLE_STAFF(data.id))
            .then((response: any) => {
                if (data.mode === 'edit') {
                    store.commit(Mutations.SET_CURRENT_STAFF_USER, response.data.data);
                    store.commit(Mutations.SET_EDIT_STAFF_VISIBILITY, true);
                } else {
                    store.commit(Mutations.SET_CURRENT_DELETE_USER, response.data.data);
                    store.commit(Mutations.SET_CONFIRM_POPUP_DATA, {
                        confirmAction: 'UserManagement/DELETE_MANAGEMENT_USER',
                        confirmActionData: { id: data.id },
                        confirmQuestionText: Vue.i18n.translate('confirmation.are-you-sure-you-want-to-delete-this-staff-user', 'Are you sure you want to delete this staff user?'),
                        confirmInfoText: `${response.data.data.first_name} ${response.data.data.family_name}`,
                        confirmButton: Vue.i18n.translate('forms.delete', 'Delete'),
                        confirmButtonClass: 'hococo-close-form-btn',
                    });
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_TENANT_USER(data: any) {
        axios.get(Endpoints.Admin.Tenants.TENANT(data.id), { params: { mode: data.mode }})
            .then((response: any) => {
                if (data.mode === 'edit') {
                    store.commit(Mutations.SET_CURRENT_TENANT_USER, response.data.data);
                    store.commit(Mutations.SET_PREVIEW_USER_PROFILE_VISIBILITY, true);
                } else if (data.mode === 'preview') {
                    store.commit(Mutations.SET_CURRENT_TENANT_USER, response.data.data);
                } else {
                    // If Primary tenant
                    if (response.data.data.owned_property !== null) {
                        store.commit(Mutations.SET_CONFIRM_POPUP_DATA, {
                            confirmAction: 'redirectToUnitLanding',
                            confirmActionData: { propertyId: response.data.data.owned_property },
                            confirmQuestionText: Vue.i18n.translate('confirmation.to-remove-this-primary-tenant-go-to-unit-and-use-move-out', 'To remove this primary tenant, go to unit and use move out.'),
                            confirmInfoText: `${response.data.data.first_name} ${response.data.data.family_name}`,
                            confirmButton: Vue.i18n.translate('general.go-to-unit', 'Go to unit'),
                            confirmButtonClass: 'hococo-register-form-btn',
                        });
                    } else {
                        store.commit(Mutations.SET_CONFIRM_POPUP_DATA, {
                            confirmAction: 'UserManagement/DELETE_TENANT_USER',
                            confirmActionData: { id: data.id },
                            confirmQuestionText: Vue.i18n.translate('confirmation.are-you-sure-you-want-to-delete-this-tenant-user', 'Are you sure you want to delete this tenant user?'),
                            confirmInfoText: `${response.data.data.first_name} ${response.data.data.family_name}`,
                            confirmButton: Vue.i18n.translate('forms.delete', 'Delete'),
                            confirmButtonClass: 'hococo-close-form-btn',
                        });
                        store.commit(Mutations.SET_CURRENT_DELETE_USER, response.data.data);
                    }
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_STAFF_USER(data: any) {
        data.staff.projects         = data.projects;
        data.staff.unit_groups      = data.unitGroups;
        data.staff.roleDescriptions = data.roleDescriptions;

        axios.patch(Endpoints.Admin.Staff.SINGLE_STAFF(data.staff.id), data.staff)
            .then(() => {
                store.dispatch(Actions.FETCH_MANAGEMENT_USERS);
                store.commit(Mutations.SET_EDIT_STAFF_VISIBILITY, false);
                store.commit(Mutations.SET_CURRENT_STAFF_USER, null);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_TENANT_USER(tenant: any) {
        axios.patch(Endpoints.Admin.Tenants.TENANT(tenant.id), tenant)
            .then((response) => {
                if (router.currentRoute.name !== 'admin-unit-details') {
                    store.dispatch(Actions.FETCH_TENANT_USERS, store.getters['UserManagement/getManagementStatusOption']);
                } else {
                    store.dispatch(Actions.FETCH_CURRENT_PROPERTY, router.currentRoute.params.unitId);
                }

                store.commit(Mutations.UPDATE_CACHED_PERSON_DATA, {
                    id: response.data.id,
                    firstName: typeof response.data.person !== 'undefined' ? response.data.person.first_name : response.data.name,
                    familyName: typeof response.data.person !== 'undefined' ? response.data.person.family_name : '',
                    phoneNumber: typeof response.data.person !== 'undefined' ? response.data.person.phone_number : '',
                });

                store.commit(Mutations.SET_PREVIEW_USER_PROFILE_VISIBILITY, false);
                store.commit(Mutations.SET_CURRENT_TENANT_USER, []);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public RESTORE_DELETED_MANAGEMENT_USER(data: any) {
        axios.patch(Endpoints.Admin.Tenants.RESTORE_DELETED_MANAGEMENT_USER(data.id))
            .then(() => {
                store.dispatch(Actions.FETCH_TENANT_USERS, store.getters['UserManagement/getManagementStatusOption']);
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_MANAGEMENT_USER(data: any) {
        axios.delete(Endpoints.Admin.Staff.SINGLE_STAFF(data.id))
            .then(() => {
                const deletedUser = store.getters['UserManagement/getCarrentDeleteUser'];

                for (const building of deletedUser.assignedBuildings) {
                    store.commit(Mutations.REMOVE_MANAGER_FROM_BUILDING, { buildingId: building.id, managerId: deletedUser.id });
                }

                store.dispatch(Actions.FETCH_MANAGEMENT_USERS);
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                store.commit(Mutations.SET_CURRENT_DELETE_USER, []);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_TENANT_USER(data: any) {
        const deletedUser = store.getters['UserManagement/getCarrentDeleteUser'];

        axios.delete(Endpoints.Admin.Tenants.TENANT(data.id))
            .then(() => {
                store.dispatch(Actions.FETCH_TENANT_USERS, this.managementStatusOption);
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                store.commit(Mutations.SET_CURRENT_DELETE_USER, []);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_TENANT_FROM_UNIT(data: any) {
        axios.delete(Endpoints.Admin.Tenants.DETACH_TENANT_FROM_UNIT(data.tenantId, data.unitId))
            .then(() => {
                store.commit(Mutations.REMOVE_TENANT_FROM_UNIT, data.tenantId);

                store.commit(Mutations.REMOVE_PROPERTY_TENANT_FROM_CACHE, {
                    userId: data.tenantId,
                    roleId: data.tenantRoleId,
                    propertyId: data.unitId,
                });

                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public REASSIGN_TENANT_USER(data: any) {
        axios.post(Endpoints.Admin.Tenants.REASSIGN_TENANT(data.unitId, data.userId), data)
            .then(() => {
                store.dispatch(Actions.FETCH_TENANT_USERS, 'inactive');
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                store.commit(Mutations.SET_REASSIGNE_TENANT_USER_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public REVERT_UPCOMING_TENANT_TO_APPLICATION(data: any) {
        return axios.post(Endpoints.Admin.Tenants.REVERT_UPCOMING_TENANT(data.ownershipId), {
            activeState: data.activeState,
        })
            .then(() => {
                store.commit(Mutations.SET_REVERT_UPCOMING_TENANT_APPLICATION_VISIBLE, false);
                NotifyHelpers.notify('upcoming_tenant_has_been_reverted', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CANCEL_UPCOMING_TENANT_MOVE_IN(ownershipId: number) {
        return axios.delete(Endpoints.Admin.Tenants.CANCEL_UPCOMING_TENANT_MOVE_IN(ownershipId))
            .then(() => {
                store.commit(Mutations.SET_CANCEL_UPCOMING_TENANT_MOVE_IN_VISIBLE, false);
                NotifyHelpers.notify('upcoming_tenant_move_in_has_been_canceled', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public SET_ADD_TENANT_VISIBILITY(visibile: boolean) {
        this.addTenantFormVisible = visibile;
    }

    @Mutation
    public SET_ADD_TENANT_FORM_MODE(mode: string) {
        this.addTenantFormMode = mode;
    }

    @Mutation
    public SET_ADD_UPCOMING_TENANT_VISIBILITY(visibile: boolean) {
        this.addUpcomingTenantFormVisible = visibile;
    }

    @Mutation
    public SET_ADD_UPCOMING_GUEST_VISIBILITY(visibile: boolean) {
        this.addUpcomingGuestFormVisible = visibile;
    }

    @Mutation
    public SET_ADD_STAFF_VISIBILITY(visibile: boolean) {
        this.addStaffFormVisible = visibile;
    }

    @Mutation
    public SET_EDIT_TENANT_VISIBILITY(visibile: boolean) {
        this.editTenantFormVisible = visibile;
    }

    @Mutation
    public SET_EDIT_STAFF_VISIBILITY(visibile: boolean) {
        this.editStaffFormVisible = visibile;
    }

    @Mutation
    public SET_USER_MANAGEMENT_STATUS_OPTION(status: string) {
        this.managementStatusOption = status;
    }

    @Mutation
    public SET_USER_MANAGEMENT_ALL_STAFF_ROLES(data: any) {
        this.allStaffRoles = data;
    }

    @Mutation
    public SET_USER_MANAGEMENT_ALL_STAFF_USERS(data: any) {
        this.allStaffUsers = data;
    }

    @Mutation
    public SET_USER_MANAGEMENT_ALL_TENANT_USERS(data: any) {
        this.allTenantUsers = data;
    }

    @Mutation
    public SET_CURRENT_TENANT_USER(data: any) {
        this.carrentTenantUser = data;
    }

    @Mutation
    public SET_CURRENT_STAFF_USER(data: any) {
        this.carrentStaffUser = data;
    }

    @Mutation
    public SET_CURRENT_DELETE_USER(data: any) {
        this.carrentDeleteUser = data;
    }

    @Mutation
    public SET_CONTACT_US_VISIBILITY(visible: boolean = true) {
        this.contactUsVisible = visible;
    }

    @Mutation
    public SET_ADD_OWNERSHIP_VISIBILITY(visible: boolean = true) {
        this.ownershipFormVisible = visible;
    }

    @Mutation
    public SET_NEW_TENANT_PROJECT_WITH_UNIT(data: any) {
        this.assignedProjectWithUnit = data;
    }

    @Mutation
    public RESET_NEW_TENANT_PROJECT_WITH_UNIT() {
        this.assignedProjectWithUnit = null;
    }

    @Mutation
    public SET_ACTIVE_UPCOMING_TENANT(upcomingTenant: any) {
        this.activeUpcomingTenant = upcomingTenant;
    }

    @Mutation
    public SET_ACTIVE_UPCOMING_GUEST(upcomingGuest: any) {
        this.activeUpcomingGuest = upcomingGuest;
    }

    @Mutation
    public REMOVE_SOCIAL_TAG(tagId: number) {
        this.carrentTenantUser.socialClubTags = this.carrentTenantUser.socialClubTags.filter((tag: any) => {
            return tag.id !== tagId;
        });
    }

    @Mutation
    public REMOVE_APPLICATION_SOCIAL_TAG(tagId: number) {
        this.carrentTenantUser.application.socialClubTags = this.carrentTenantUser.application.socialClubTags.filter((tag: any) => {
            return tag.id !== tagId;
        });
    }

    @Mutation
    public ADD_SOCIAL_TAG(tag: any) {
        this.carrentTenantUser.socialClubTags.push(tag);
    }

    @Mutation
    public ADD_APPLICATION_SOCIAL_TAG(tag: any) {
        this.carrentTenantUser.application.socialClubTags.push(tag);
    }

    @Mutation
    public REMOVE_EDUCATIONAL_TAG(tagId: number) {
        this.carrentTenantUser.educationTags = this.carrentTenantUser.educationTags.filter((tag: any) => {
            return tag.id !== tagId;
        });
    }

    @Mutation
    public REMOVE_APPLICATION_EDUCATIONAL_TAG(tagId: number) {
        this.carrentTenantUser.application.educationTags = this.carrentTenantUser.application.educationTags.filter((tag: any) => {
            return tag.id !== tagId;
        });
    }

    @Mutation
    public ADD_EDUCATIONAL_TAG(tag: any) {
        this.carrentTenantUser.educationTags = [];
        this.carrentTenantUser.educationTags.push(tag);
    }

    @Mutation
    public ADD_APPLICATION_EDUCATIONAL_TAG(tag: any) {
        this.carrentTenantUser.application.educationTags = [];
        this.carrentTenantUser.application.educationTags.push(tag);
    }

    @Mutation
    public RESET_PROFILING_APPLICATION_VARIANTS_AND_BUILDINGS(tag: any) {
        this.carrentTenantUser.application.buildings = [];
        this.carrentTenantUser.application.variants = [];
    }

    @Mutation
    public SET_REASSIGNE_TENANT_USER_VISIBILITY(visible: boolean) {
        this.reassignTenantUserVisible = visible;
    }

    @Mutation
    public SET_INACTIVE_USER_ID(id: number) {
        this.inactiveUserId = id;
    }

    @Mutation
    public SET_REVERT_UPCOMING_TENANT_APPLICATION_VISIBLE(value: boolean) {
        this.revertUpcomingTenantApplicationVisible = value;
    }

    @Mutation
    public SET_CANCEL_UPCOMING_TENANT_MOVE_IN_VISIBLE(value: boolean) {
        this.cancelUpcomingTenantMoveInVisible = value;
    }

    @Mutation
    public SET_CANCEL_UPCOMING_GUEST_MOVE_IN_VISIBLE(value: boolean) {
        this.cancelUpcomingGuestMoveInVisible = value;
    }
}
